import { useMemo, useState } from "react";
import { CheckCircle2, ClipboardList } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { formatPickupScheduledAt } from "@gaole/shared";
import { api, ApiError } from "../lib/api";
import { num, rm, timeAgo, fmtDate } from "../lib/format";
import { toast } from "../stores/toast";
import { useShellTabs } from "../stores/shell-tabs";
import { Badge, Button, PillSegmentSwitcher, statusColor } from "../components/ui";
import { DataTable, type Column } from "../components/DataTable";
import { ListPageShell } from "../components/shell/ListPageShell";
import { PickupCalendarTab } from "./fulfilment/PickupCalendarTab";

type Tab = "queue" | "picklists" | "calendar";

interface QueueOrder {
  id: string;
  orderNo: string;
  kind: string;
  customer: string;
  itemCount: number;
  total: number;
  paidAt: string | null;
  fulfilmentMethod?: string;
  pickupLocationName?: string | null;
  pickupScheduledAt?: string | null;
}

type QueueMethodFilter = "ALL" | "DELIVERY" | "PICKUP";

interface PickListSummary {
  id: string;
  code: string;
  status: string;
  orderCount: number;
  createdAt: string;
  completedAt: string | null;
}

const KIND_LABEL: Record<string, string> = {
  STORE: "Shop",
  RAFFLE_WIN: "Raffle win",
  AUCTION_WIN: "Auction win",
  VAULT_SHIPMENT: "Vault shipment",
};

const FULFILMENT_TABS: { value: Tab; label: string }[] = [
  { value: "queue", label: "Queue" },
  { value: "picklists", label: "Pick lists" },
  { value: "calendar", label: "Calendar" },
];

export function Fulfilment() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const openInNewTab = useShellTabs((s) => s.openInNewTab);

  const [tab, setTab] = useState<Tab>("queue");
  const [queueMethod, setQueueMethod] = useState<QueueMethodFilter>("ALL");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const queueQuery = useQuery({
    queryKey: ["fulfilment-queue", queueMethod],
    queryFn: () => {
      const params = queueMethod === "ALL" ? "" : `?method=${queueMethod}`;
      return api<{ items: QueueOrder[] }>(`/fulfilment/queue${params}`);
    },
    enabled: tab === "queue",
  });

  const pickListsQuery = useQuery({
    queryKey: ["fulfilment-picklists"],
    queryFn: () => api<{ items: PickListSummary[] }>("/fulfilment/picklists"),
    enabled: tab === "picklists",
  });

  const createPickListMutation = useMutation({
    mutationFn: (orderIds: string[]) =>
      api<{ id: string; code: string }>("/fulfilment/picklists", { method: "POST", body: { orderIds } }),
    onSuccess: (data) => {
      toast("success", `Pick list ${data.code} created`);
      setSelected(new Set());
      setTab("picklists");
      void queryClient.invalidateQueries({ queryKey: ["fulfilment-queue"] });
      void queryClient.invalidateQueries({ queryKey: ["fulfilment-picklists"] });
      const path = openInNewTab(`/fulfilment/picklists/${data.id}?tab=pick`);
      navigate(path);
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Create failed"),
  });

  function openPickList(id: string) {
    const path = openInNewTab(`/fulfilment/picklists/${id}?tab=pick`);
    navigate(path);
  }

  const queueRows = queueQuery.data?.items ?? [];

  const queueColumns: Column<QueueOrder>[] = useMemo(
    () => [
      {
        key: "orderNo",
        header: "Order#",
        sortable: true,
        sortValue: (r) => r.orderNo,
        render: (r) => <span className="font-mono text-xs">{r.orderNo}</span>,
      },
      {
        key: "customer",
        header: "Customer",
        sortable: true,
        sortValue: (r) => r.customer,
        render: (r) => <span className="text-sm">{r.customer}</span>,
      },
      {
        key: "fulfilment",
        header: "Method",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.fulfilmentMethod ?? "DELIVERY",
        render: (r) =>
          r.fulfilmentMethod === "PICKUP" ? (
            <Badge color="blue">Pickup</Badge>
          ) : (
            <span className="text-xs text-muted">Delivery</span>
          ),
      },
      {
        key: "pickup",
        header: "Pickup",
        hideSm: true,
        render: (r) =>
          r.fulfilmentMethod === "PICKUP" && r.pickupLocationName ? (
            <div className="text-xs text-muted">
              <p className="font-medium text-foreground">{r.pickupLocationName}</p>
              {r.pickupScheduledAt && (
                <p>{formatPickupScheduledAt(r.pickupScheduledAt)}</p>
              )}
            </div>
          ) : (
            <span className="text-xs text-muted">—</span>
          ),
      },
      {
        key: "kind",
        header: "Kind",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.kind,
        exportValue: (r) => KIND_LABEL[r.kind] ?? r.kind,
        render: (r) => <span className="text-xs text-white/60">{KIND_LABEL[r.kind] ?? r.kind}</span>,
      },
      {
        key: "items",
        header: "Items",
        className: "text-right",
        sortable: true,
        sortValue: (r) => r.itemCount,
        render: (r) => <span className="text-xs">{num(r.itemCount)}</span>,
      },
      {
        key: "total",
        header: "Total",
        className: "text-right",
        sortable: true,
        sortValue: (r) => r.total,
        exportValue: (r) => r.total,
        render: (r) => <span className="font-display font-semibold">{rm(r.total)}</span>,
      },
      {
        key: "paid",
        header: "Paid",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.paidAt ?? "",
        exportValue: (r) => (r.paidAt ? fmtDate(r.paidAt) : ""),
        render: (r) => (
          <span className="text-xs text-white/50" title={r.paidAt ? fmtDate(r.paidAt) : undefined}>
            {r.paidAt ? timeAgo(r.paidAt) : "—"}
          </span>
        ),
      },
    ],
    [],
  );

  const pickListColumns: Column<PickListSummary>[] = useMemo(
    () => [
      {
        key: "code",
        header: "Pick list",
        sortable: true,
        sortValue: (r) => r.code,
        render: (r) => <span className="font-mono text-xs font-semibold">{r.code}</span>,
      },
      {
        key: "status",
        header: "Status",
        sortable: true,
        sortValue: (r) => r.status,
        render: (r) => <Badge color={statusColor(r.status)}>{r.status}</Badge>,
      },
      {
        key: "orders",
        header: "Orders",
        className: "text-right",
        sortable: true,
        sortValue: (r) => r.orderCount,
        render: (r) => <span className="text-xs">{num(r.orderCount)}</span>,
      },
      {
        key: "created",
        header: "Created",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.createdAt,
        exportValue: (r) => fmtDate(r.createdAt),
        render: (r) => <span className="text-xs text-white/50">{fmtDate(r.createdAt)}</span>,
      },
      {
        key: "completed",
        header: "Completed",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.completedAt ?? "",
        exportValue: (r) => (r.completedAt ? fmtDate(r.completedAt) : ""),
        render: (r) => (
          <span className="text-xs text-white/50">{r.completedAt ? fmtDate(r.completedAt) : "—"}</span>
        ),
      },
    ],
    [],
  );

  const toolbarViews = useMemo(
    () => ({
      options: FULFILMENT_TABS.map((t) =>
        t.value === "queue" ? { ...t, badge: queueQuery.data?.items.length } : t,
      ),
      value: tab,
      onChange: (v: string) => setTab(v as Tab),
    }),
    [tab, queueQuery.data?.items.length],
  );

  const queueSelection = useMemo(
    () =>
      tab === "queue"
        ? {
            trailing: (
              <Button
                size="sm"
                disabled={createPickListMutation.isPending || selected.size === 0}
                onClick={() => createPickListMutation.mutate([...selected])}
              >
                Create pick list ({selected.size})
              </Button>
            ),
          }
        : undefined,
    [tab, selected, createPickListMutation.isPending],
  );

  return (
    <ListPageShell>
      {tab === "calendar" ? (
        <div className="flex min-h-0 flex-1 flex-col gap-4">
          <div className="shrink-0">
            <PillSegmentSwitcher
              options={FULFILMENT_TABS.map((t) =>
                t.value === "queue" ? { ...t, badge: queueQuery.data?.items.length } : t,
              )}
              value={tab}
              onChange={(v) => setTab(v as Tab)}
            />
          </div>
          <PickupCalendarTab />
        </div>
      ) : tab === "queue" ? (
        <DataTable
          columns={queueColumns}
          rows={queueRows}
          rowKey={(r) => r.id}
          loading={queueQuery.isLoading}
          exportFilename="fulfilment-queue"
          selectable
          selectedKeys={selected}
          onSelectedKeysChange={setSelected}
          selection={queueSelection}
          toolbar={{
            views: toolbarViews,
            trailing:
              tab === "queue" ? (
                <PillSegmentSwitcher
                  options={[
                    { value: "ALL", label: "All" },
                    { value: "DELIVERY", label: "Delivery" },
                    { value: "PICKUP", label: "Pickup" },
                  ]}
                  value={queueMethod}
                  onChange={(v) => setQueueMethod(v as QueueMethodFilter)}
                />
              ) : undefined,
          }}
          empty={{
            icon: CheckCircle2,
            title: "Queue is clear",
            hint: "No paid orders waiting for fulfilment.",
          }}
        />
      ) : (
        <DataTable
          columns={pickListColumns}
          rows={pickListsQuery.data?.items}
          rowKey={(r) => r.id}
          loading={pickListsQuery.isLoading}
          exportFilename="fulfilment-picklists"
          toolbar={{ views: toolbarViews }}
          onRowClick={(r) => openPickList(r.id)}
          empty={{
            icon: ClipboardList,
            title: "No pick lists yet",
            hint: "Select orders from the queue and create a pick list.",
          }}
        />
      )}
    </ListPageShell>
  );
}
